// ElectricalDashboard — hero for AI Load-Calc + Next Stop + Funnel counts.
// Mirrors PlumbingDashboard shape, yellow accent, panel-snap hero.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { AdminShell } from "@/components/Layout";
import { toast } from "sonner";
import {
  Lightning, Sparkle, MapPin, Clock, ArrowRight, CheckCircle, PaperPlaneTilt,
  Warning, House, Camera, UploadSimple,
} from "@phosphor-icons/react";

const STATUS_CHIP = {
  scheduled:  { cls: "bg-slate-200 text-slate-700",    label: "SCHEDULED" },
  en_route:   { cls: "bg-cyan-100 text-cyan-800",      label: "EN ROUTE" },
  diagnosed:  { cls: "bg-yellow-100 text-yellow-800",  label: "SIZED" },
  in_progress:{ cls: "bg-amber-100 text-amber-800",    label: "IN PROGRESS" },
  completed:  { cls: "bg-emerald-100 text-emerald-800",label: "COMPLETED" },
};

export default function ElectricalDashboard() {
  const nav = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try { const r = await api.get("/electrical/electrical-jobs"); setJobs(r.data.jobs || []); }
      catch { toast.error("Could not load jobs"); }
      finally { setLoading(false); }
    })();
  }, []);

  const handleDropPhoto = (e) => {
    e.preventDefault();
    const f = e.dataTransfer?.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        sessionStorage.setItem("pending_loadcalc_photo", JSON.stringify({
          name: f.name, type: f.type, data: reader.result,
        }));
        nav("/admin/electrical/load-calc");
      } catch { toast.error("Could not open Load-Calc"); }
    };
    reader.readAsDataURL(f);
  };

  const nextStop = jobs.find(j => j.status === "en_route") || jobs.find(j => j.status === "scheduled");
  const stops = jobs.filter(j => j.id !== nextStop?.id).slice(0, 4);
  const funnel = {
    new: jobs.filter(j => j.status === "scheduled" && !j.has_load_calc).length,
    sized: jobs.filter(j => j.has_load_calc).length,
    en_route: jobs.filter(j => j.status === "en_route").length,
    completed: jobs.filter(j => j.status === "completed").length,
  };

  return (
    <AdminShell>
      <div className="p-4 sm:p-6 max-w-4xl mx-auto space-y-4" data-testid="electrical-dashboard">
        {/* HERO */}
        <div
          onDragOver={e => e.preventDefault()}
          onDrop={handleDropPhoto}
          onClick={() => nav("/admin/electrical/load-calc")}
          data-testid="electrical-hero-snap"
          className="cursor-pointer relative rounded-2xl overflow-hidden bg-gradient-to-br from-slate-900 via-yellow-900 to-slate-950 p-6 sm:p-8 text-white hover:shadow-xl transition group"
        >
          <div className="absolute inset-0 opacity-10 pointer-events-none">
            <div className="absolute top-6 right-6"><Lightning weight="fill" size={200}/></div>
          </div>
          <div className="relative">
            <div className="text-[10px] font-black uppercase tracking-widest text-yellow-200 inline-flex items-center gap-1.5">
              <Sparkle weight="fill" size={11}/> Claude Vision · NEC 220 · Live
            </div>
            <h1 className="font-display font-black text-3xl sm:text-4xl leading-tight mt-2 tracking-tight" data-testid="electrical-hero-title">
              Snap the panel.<br/>
              <span className="text-yellow-300">I'll size the service.</span>
            </h1>
            <div className="text-sm text-yellow-100/80 mt-2 max-w-md">
              Drop a panel or nameplate photo — get a NEC-220 load calc, recommended service size, and a customer estimate in ~8 seconds.
            </div>
            <div className="mt-4 flex items-center gap-2 flex-wrap">
              <div className="inline-flex items-center gap-1.5 h-10 px-3 rounded-lg bg-yellow-400 text-slate-900 font-black uppercase text-[11px] tracking-widest group-hover:scale-105 transition">
                <UploadSimple weight="bold" size={14}/> Drop a photo
              </div>
              <div className="inline-flex items-center gap-1.5 h-10 px-3 rounded-lg bg-white/10 border border-white/20 text-white font-black uppercase text-[11px] tracking-widest">
                <Camera weight="bold" size={14}/> or tap to open load-calc
              </div>
            </div>
          </div>
        </div>

        {/* NEXT STOP */}
        {nextStop && (
          <div className="rounded-xl bg-white border-2 border-slate-200 overflow-hidden" data-testid="electrical-next-stop">
            <div className="px-4 py-2 bg-slate-900 text-white flex items-center gap-2">
              <MapPin weight="fill" size={14}/>
              <div className="text-[10px] font-black uppercase tracking-widest">Next stop</div>
              <StatusChip s={nextStop.status}/>
              {nextStop.has_load_calc && (
                <span className="text-[9px] font-black uppercase tracking-widest bg-yellow-500 text-slate-900 px-1.5 py-0.5 rounded ml-auto inline-flex items-center gap-1">
                  <Sparkle weight="fill" size={9}/> AI sized
                </span>
              )}
            </div>
            <div className="p-4">
              <div className="font-display font-black text-xl leading-tight" data-testid="next-stop-title">{nextStop.title}</div>
              <div className="text-sm text-slate-500 font-medium inline-flex items-center gap-1 mt-1">
                <House size={12}/> {nextStop.address}
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <button type="button" onClick={() => nav(`/admin/electrical/load-calc?job=${nextStop.id}`)}
                  data-testid="next-stop-loadcalc"
                  className="h-10 px-3 rounded-lg bg-yellow-500 hover:bg-yellow-600 text-slate-900 text-[11px] font-black uppercase tracking-widest inline-flex items-center gap-1.5">
                  <Sparkle weight="fill" size={13}/> {nextStop.has_load_calc ? "Re-run Load-Calc" : "Run AI Load-Calc"}
                </button>
                <a href={`https://maps.google.com/?q=${encodeURIComponent(nextStop.address)}`}
                  target="_blank" rel="noopener noreferrer"
                  data-testid="next-stop-nav"
                  className="h-10 px-3 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-[11px] font-black uppercase tracking-widest inline-flex items-center gap-1.5">
                  <MapPin weight="fill" size={13}/> Navigate
                </a>
                <button type="button" onClick={() => nav(`/admin/jobs?open=${nextStop.id}`)}
                  data-testid="next-stop-open"
                  className="h-10 px-3 rounded-lg bg-white border-2 border-slate-200 hover:border-slate-900 text-slate-800 text-[11px] font-black uppercase tracking-widest inline-flex items-center gap-1.5">
                  Open job <ArrowRight size={12}/>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* TODAY'S ROUTE */}
        <div className="rounded-xl bg-white border-2 border-slate-200 overflow-hidden" data-testid="electrical-today-stops">
          <div className="px-4 py-2 border-b border-slate-100 flex items-center justify-between">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-600 inline-flex items-center gap-1.5">
              <Clock size={12}/> Today's route
            </div>
            <button type="button" onClick={() => nav("/admin/jobs")}
              data-testid="electrical-all-jobs"
              className="text-[10px] font-black uppercase tracking-widest text-yellow-600 hover:text-yellow-800 inline-flex items-center gap-1">
              All jobs <ArrowRight size={10}/>
            </button>
          </div>
          {loading ? (
            <div className="p-6 text-sm text-slate-500 font-medium">Loading…</div>
          ) : stops.length === 0 ? (
            <div className="p-6 text-sm text-slate-500 font-medium" data-testid="electrical-no-stops">
              Nothing else on today's list. Great work.
            </div>
          ) : (
            stops.map((j, i) => (
              <div key={j.id} className={`p-3 flex items-center gap-3 ${i > 0 ? "border-t border-slate-100" : ""}`}
                    data-testid={`electrical-stop-${j.id}`}>
                <div className="h-9 w-9 rounded-lg bg-yellow-50 text-yellow-600 flex items-center justify-center shrink-0">
                  <Lightning weight="fill" size={16}/>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-black text-sm truncate">{j.title}</div>
                  <div className="text-[11px] text-slate-500 truncate">{j.address}</div>
                </div>
                <div className="flex items-center gap-1.5">
                  <StatusChip s={j.status}/>
                  {j.has_load_calc && (
                    <span className="text-[9px] font-black uppercase tracking-widest bg-yellow-100 text-yellow-800 px-1.5 py-0.5 rounded inline-flex items-center gap-0.5">
                      <Sparkle weight="fill" size={8}/> AI
                    </span>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {/* FUNNEL */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="electrical-funnel">
          <FunnelTile label="New calls" value={funnel.new} icon={<Warning size={14}/>} accent="slate"/>
          <FunnelTile label="AI sized" value={funnel.sized} icon={<Sparkle size={14}/>} accent="yellow"/>
          <FunnelTile label="En route" value={funnel.en_route} icon={<PaperPlaneTilt size={14}/>} accent="cyan"/>
          <FunnelTile label="Completed" value={funnel.completed} icon={<CheckCircle size={14}/>} accent="emerald"/>
        </div>
      </div>
    </AdminShell>
  );
}

function StatusChip({ s }) {
  const m = STATUS_CHIP[s] || { cls: "bg-slate-100 text-slate-600", label: (s || "").toUpperCase() };
  return <span className={`text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded ${m.cls}`}>{m.label}</span>;
}

function FunnelTile({ label, value, icon, accent }) {
  const bg = {
    slate:   "bg-slate-50 border-slate-200 text-slate-800",
    yellow:  "bg-yellow-50 border-yellow-200 text-yellow-900",
    cyan:    "bg-cyan-50 border-cyan-200 text-cyan-900",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
  }[accent] || "bg-slate-50 border-slate-200 text-slate-800";
  return (
    <div className={`rounded-xl border-2 p-3 ${bg}`}>
      <div className="text-[9px] font-black uppercase tracking-widest opacity-70 inline-flex items-center gap-1">{icon} {label}</div>
      <div className="font-display font-black text-2xl mt-0.5">{value}</div>
    </div>
  );
}
